import React, { useEffect, useMemo, useRef, useState } from "react";
import Popover from "@mui/material/Popover";
import { FiChevronDown } from "react-icons/fi";
import { IoMdCheckbox } from "react-icons/io";
import SearchLogo from "../../../Assests/EmployeeManagement/Search.svg";
import CrossIconsLogo from "../../../Assests/EmployeeManagement/crossIcons.svg";
import { getTaxCategoryDisplayLabels } from "./taxCategorySelection";

// Searchable checkbox dropdown; reuses the Employee Management
// Store/Role dropdown chrome (qv-emp-dropdown-* in employeeWorkspace.css).
const TaxCategoryMultiSelect = ({
  options = [],
  selectedItems = [],
  onChange,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef(null);
  const open = Boolean(anchorEl);

  const selectedIds = useMemo(
    () => new Set(selectedItems.map((item) => String(item.id))),
    [selectedItems],
  );
  const allSelected =
    options.length > 0 && selectedItems.length === options.length;
  const labels = getTaxCategoryDisplayLabels(selectedItems, options);
  const label = labels.length ? labels.join(", ") : "--Choose a Category--";

  const filteredOptions = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return options;
    return options.filter((item) =>
      String(item.title || "").toLowerCase().includes(query),
    );
  }, [options, searchQuery]);

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => searchInputRef.current?.focus());
    }
  }, [open]);

  const handleClose = () => {
    setAnchorEl(null);
    setSearchQuery("");
  };

  const toggleOption = (option) => {
    const id = String(option.id);
    const nextIds = new Set(selectedIds);
    if (nextIds.has(id)) nextIds.delete(id);
    else nextIds.add(id);
    onChange(options.filter((item) => nextIds.has(String(item.id))));
  };

  const toggleAll = () => {
    onChange(allSelected ? [] : options.map((item) => ({ ...item })));
  };

  return (
    <div>
      <button
        type="button"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        className="qv-emp-dropdown-trigger qv-tax-category-trigger w-full h-[44px] px-3 rounded-[8px] text-left flex items-center justify-between gap-2"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span
          className={`truncate ${labels.length ? "" : "qv-tax-category-trigger__placeholder"}`}
        >
          {label}
        </span>
        <FiChevronDown className="w-4 h-4 flex-shrink-0" />
      </button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        PaperProps={{
          className:
            "qv-emp-dropdown-menu rounded-xl shadow-lg p-2 overflow-hidden flex flex-col",
          style: {
            width: anchorEl?.offsetWidth || 320,
            maxHeight: "320px",
          },
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        {/* Search */}
        <div className="relative mb-2 px-1">
          <img
            src={SearchLogo}
            alt=""
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
          />
          <input
            ref={searchInputRef}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search Category..."
            className="qv-emp-dropdown-search w-full pl-9 pr-8 py-2 rounded-lg"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2"
              aria-label="Clear search"
            >
              <img src={CrossIconsLogo} alt="" className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto px-1 space-y-1" role="listbox">
          {options.length > 1 && !searchQuery.trim() && (
            <button
              type="button"
              onClick={toggleAll}
              className="qv-emp-dropdown-option w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left"
            >
              <div className="w-6 h-6 border rounded flex items-center justify-center flex-shrink-0">
                {allSelected && (
                  <IoMdCheckbox className="w-full h-full scale-[1.3] text-primary" />
                )}
              </div>
              <span className="font-bold">Select All</span>
            </button>
          )}

          {filteredOptions.length ? (
            filteredOptions.map((option) => (
              <button
                type="button"
                key={option.id}
                onClick={() => toggleOption(option)}
                className="qv-emp-dropdown-option w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left"
              >
                <div className="w-6 h-6 border rounded flex items-center justify-center flex-shrink-0">
                  {selectedIds.has(String(option.id)) && (
                    <IoMdCheckbox className="w-full h-full scale-[1.3] text-primary" />
                  )}
                </div>
                <span className="font-bold">{option.title}</span>
              </button>
            ))
          ) : (
            <div className="text-center py-6 text-sm text-muted-foreground font-bold">
              {searchQuery.trim()
                ? `No Category found matching "${searchQuery}"`
                : "No Category found"}
            </div>
          )}
        </div>

        {/* Clear */}
        {selectedItems.length > 0 && (
          <div className="border-t pt-2 px-1">
            <button
              type="button"
              onClick={() => onChange([])}
              className="w-full py-2 text-sm qv-emp-dropdown-option rounded font-bold text-muted-foreground"
            >
              Clear Selection
            </button>
          </div>
        )}
      </Popover>
    </div>
  );
};

export default TaxCategoryMultiSelect;
